import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, Sparkles, ChevronRight, Gift, Trophy } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const FeatureDiscoveryPrompt = () => {
  const [featureData, setFeatureData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeaturePrompts();
  }, []);

  const fetchFeaturePrompts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/user/feature-prompts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeatureData(response.data);
    } catch (error) {
      console.error('Error fetching feature prompts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTryFeature = async (featureId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/user/award-feature-badge/${featureId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.awarded) {
        toast.success(`${response.data.message}`, {
          icon: '🎉',
          duration: 5000
        });
      }
    } catch (error) {
      console.error('Error awarding badge:', error);
    }
  };

  if (loading || dismissed || !featureData?.featured_prompt) {
    return null;
  }

  const { featured_prompt, discovery_progress } = featureData;

  return (
    <Card className="card-base border-l-4 border-l-amber-400 bg-gradient-to-r from-amber-50 to-transparent mb-6 relative overflow-hidden">
      <button 
        onClick={() => setDismissed(true)}
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 z-10"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
      
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className="h-14 w-14 rounded-2xl bg-amber-100 flex items-center justify-center flex-shrink-0 text-2xl">
            {featured_prompt.icon}
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-amber-600 text-sm font-medium flex items-center gap-1">
                <Sparkles className="h-3.5 w-3.5" />
                Did you know?
              </span>
              <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700 border-amber-200">
                +{featured_prompt.points_reward} pts
              </Badge>
            </div>
            
            <h3 className="font-heading font-semibold text-forest text-lg mb-1">
              {featured_prompt.title}
            </h3>
            
            <p className="text-gray-600 text-sm mb-3">
              {featured_prompt.description}
            </p>
            
            <div className="flex items-center gap-3">
              <Link to={featured_prompt.link} onClick={() => handleTryFeature(featured_prompt.id)}>
                <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white">
                  Try It Now <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
              
              {/* Progress indicator */}
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Trophy className="h-3.5 w-3.5 text-amber-500" />
                <span>{discovery_progress.discovered}/{discovery_progress.total} features discovered</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="mt-4 h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full transition-all duration-500"
            style={{ width: `${discovery_progress.percent}%` }}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default FeatureDiscoveryPrompt;
